import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { MdSettings, MdPayment, MdNotifications, MdPalette, MdImage, MdCheck, MdDescription, MdInfo, MdPrint, MdVisibility, MdVisibilityOff, MdInventory2, MdDesignServices, MdSmartToy, MdUploadFile } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { configuracionAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useConfig } from '../context/ConfigContext';

const SettingsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const SettingsLayout = styled.div`
  display: grid;
  grid-template-columns: 250px minmax(0, 1fr);
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const SettingsSidebar = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  box-shadow: ${props => props.theme.shadows.card};
  height: fit-content;
`;

const SidebarItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: 9px 12px;
  background-color: ${props => props.active ? props.theme.colors.backgroundDark : 'transparent'};
  color: ${props => props.active ? props.theme.colors.textPrimary : props.theme.colors.textSecondary};
  border: none;
  border-radius: 10px;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.active ? 600 : 500};
  font-family: ${props => props.theme.fonts.primary};
  text-align: left;
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  margin-bottom: 2px;
  box-shadow: ${props => props.active ? props.theme.shadows.sm : 'none'};

  svg {
    font-size: 1.2rem;
    color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.textLight};
  }

  &:hover {
    background-color: ${props => props.theme.colors.backgroundDark};
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const SettingsContent = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.card};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.sm} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const SectionDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.xl} 0;
`;

const FormSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const LogoPreview = styled.div`
  width: 120px;
  height: 120px;
  background-color: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.theme.colors.border};
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    padding: ${props => props.theme.spacing.sm};
  }
  
  svg {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 3rem;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const LogoInfo = styled.div`
  flex: 1;
`;

const LogoTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.md};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
`;

const LogoDescription = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0 0 ${props => props.theme.spacing.md} 0;
`;

const UploadButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundLight};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.theme.fontWeights.medium};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textLight};
  }
`;

const SaveButton = styled.button`
  padding: 10px ${props => props.theme.spacing.xl};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  box-shadow: 0 4px 14px ${props => props.theme.colors.primary}40;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px ${props => props.theme.colors.primary}50;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  border: 1px solid #c3e6cb;
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.lg};
  border: 1px solid #f5c6cb;
`;

const Select = styled.select`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:focus {
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }
`;

const InfoText = styled.p`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  margin-top: ${props => props.theme.spacing.xs};
  
  svg {
    color: ${props => props.theme.colors.primary};
  }
`;

const SubsectionTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.lg} 0;
  font-weight: ${props => props.theme.fontWeights.semibold};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 2px solid ${props => props.theme.colors.border};
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: ${props => props.theme.colors.primary};
`;

const CheckboxLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  cursor: pointer;
  user-select: none;
`;

const TextArea = styled.textarea`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-family: inherit;
  resize: vertical;
  transition: ${props => props.theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }

  &::placeholder {
    color: ${props => props.theme.colors.textSecondary};
  }
`;

const ColorPickerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const ColorPreviewWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const ColorPreview = styled.div`
  width: 60px;
  height: 60px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.color};
  border: 2px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: ${props => props.theme.shadows.md};
  }
`;

const ColorInput = styled.input`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  font-family: 'Courier New', monospace;
  transition: ${props => props.theme.transitions.fast};
  text-transform: uppercase;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    background-color: ${props => props.theme.colors.backgroundLight};
  }
`;

const ColorPaletteGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(45px, 1fr));
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.sm};
`;

const ColorOption = styled.button`
  width: 45px;
  height: 45px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.color};
  border: 2px solid ${props => props.selected ? props.theme.colors.textPrimary : props.theme.colors.border};
  cursor: pointer;
  transition: ${props => props.theme.transitions.fast};
  box-shadow: ${props => props.theme.shadows.sm};
  position: relative;

  &:hover {
    transform: scale(1.1);
    box-shadow: ${props => props.theme.shadows.md};
  }

  ${props => props.selected && `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 20px;
      font-weight: bold;
      text-shadow: 0 0 3px rgba(0,0,0,0.5);
    }
  `}
`;

const ColorLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const PreviewNote = styled.div`
  background-color: ${props => props.theme.colors.backgroundLight};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  
  svg {
    flex-shrink: 0;
  }
`;

const BusinessTypeCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.xl};
  border: 2px solid ${props => props.$active ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? props.theme.colors.primary + '10' : props.theme.colors.background};
  flex: 1;
  min-width: 160px;

  svg { font-size: 2.5rem; color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textLight}; }
  h4 { margin: 0; font-size: ${props => props.theme.fontSizes.lg}; color: ${props => props.$active ? props.theme.colors.primary : props.theme.colors.textPrimary}; font-weight: 700; }
  p { margin: 0; font-size: ${props => props.theme.fontSizes.sm}; color: ${props => props.theme.colors.textSecondary}; text-align: center; }

  &:hover { border-color: ${props => props.theme.colors.primary}; }
`;

const BusinessTypeRow = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  margin: ${props => props.theme.spacing.xl} 0;
  flex-wrap: wrap;
`;

const Settings = () => {
  const { themeName, setTheme, primaryColor, setPrimaryColor } = useTheme();
  const { refreshConfig } = useConfig();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('general');
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = React.useRef(null);
  
  // Formulario de configuración
  const [formData, setFormData] = useState({
    nombre_empresa: '',
    direccion: '',
    email_contacto: '',
    telefono: '',
    logo_url: '',
    moneda: '',
    dias_gracia: 7,
    prefijo_factura: '',
    codigo_cancelacion_recibos: '',
    iva: 0,
    email_notificaciones: '',
    recordatorio_dias: 3,
    frecuencia_reportes: '',
    tema: '',
    color_principal: '',
    idioma: '',
    // Campos de plantillas de recibo
    plantilla_recibo_mostrar_logo: true,
    plantilla_recibo_mostrar_direccion: true,
    plantilla_recibo_mostrar_telefono: true,
    plantilla_recibo_mostrar_email: true,
    plantilla_recibo_pie_pagina: '',
    plantilla_recibo_notas_adicionales: '',
    plantilla_recibo_terminos: '',
    // Campos de plantillas de comprobante
    plantilla_comprobante_mostrar_logo: true,
    plantilla_comprobante_mostrar_firma: false,
    plantilla_comprobante_pie_pagina: '',
    plantilla_comprobante_mensaje_agradecimiento: 'Gracias por su pago',
    // Formatos
    formato_fecha: 'DD/MM/YYYY',
    formato_moneda: '$0,0.00',
    mostrar_sello_digital: false,
    texto_sello_digital: '',
    // Formato de impresión
    formato_impresion: 'normal', // 'normal' o 'termica'
    ancho_papel_termico: 80, // 58mm o 80mm
    // yCloud WhatsApp
    ycloud_api_key: '',
    ycloud_whatsapp_numero: '',
    ycloud_plantilla_nombre: '',
    ycloud_plantilla_idioma: 'es',
    tipo_negocio: 'productos',
    // IA
    ai_provider: 'anthropic',
    ai_api_key: '',
    ai_model: 'claude-sonnet-5',
    // Pestañas visibles
    tabs_ocultas: '[]',
    ocultar_tabs_admin: false,
  });

  const [showApiKey, setShowApiKey] = useState(false);

  // Cargar configuración al montar el componente
  useEffect(() => {
    loadConfiguracion();
  }, []);

  const loadConfiguracion = async () => {
    try {
      setLoading(true);
      const response = await configuracionAPI.getConfiguracion();
      const configData = response.data.data;
      setConfig(configData);
      setFormData(configData);
      
      // Sincronizar tema si existe en la configuración
      if (configData.tema) {
        setTheme(configData.tema);
      }
      
      // Sincronizar color principal SOLO al cargar inicialmente
      // (no cuando el usuario está editando)
      if (configData.color_principal && !formData.color_principal) {
        setPrimaryColor(configData.color_principal);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
      setErrorMessage('Error al cargar la configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleColorChange = (newColor) => {
    // Validar formato hex
    const hexRegex = /^#[0-9A-F]{6}$/i;
    if (hexRegex.test(newColor)) {
      setFormData(prev => ({
        ...prev,
        color_principal: newColor
      }));
      // NO aplicar el color inmediatamente, solo actualizar el formulario
    }
  };

  const handleColorInputChange = (e) => {
    let value = e.target.value.toUpperCase();
    
    // Asegurar que comience con #
    if (!value.startsWith('#')) {
      value = '#' + value;
    }
    
    // Limitar a 7 caracteres (#RRGGBB)
    value = value.slice(0, 7);
    
    setFormData(prev => ({
      ...prev,
      color_principal: value
    }));
    
    // NO aplicar el color, solo actualizar el formulario
  };

  const presetColors = [
    '#5B9BD5', // Azul (default)
    '#2196F3', // Azul Material
    '#4CAF50', // Verde
    '#FF9800', // Naranja
    '#F44336', // Rojo
    '#9C27B0', // Púrpura
    '#00BCD4', // Cyan
    '#FF5722', // Rojo profundo
    '#795548', // Marrón
    '#607D8B', // Gris azulado
    '#E91E63', // Rosa
    '#3F51B5', // Índigo
  ];

  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setErrorMessage('Por favor selecciona una imagen válida (PNG, JPG, GIF, WEBP)');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    // Validar tamaño (máximo 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage('La imagen debe ser menor a 2MB');
      setTimeout(() => setErrorMessage(''), 5000);
      return;
    }

    // Convertir a base64 para preview y guardado
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = reader.result;
      setFormData(prev => ({
        ...prev,
        logo_url: base64String
      }));
    };
    reader.onerror = () => {
      setErrorMessage('Error al cargar la imagen');
      setTimeout(() => setErrorMessage(''), 5000);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSuccessMessage('');
      setErrorMessage('');
      
      await configuracionAPI.updateConfiguracion(formData);
      
      // Aplicar el color principal DESPUÉS de guardar exitosamente
      if (formData.color_principal) {
        const hexRegex = /^#[0-9A-F]{6}$/i;
        if (hexRegex.test(formData.color_principal)) {
          setPrimaryColor(formData.color_principal);
        }
      }
      
      setSuccessMessage('Configuración guardada exitosamente');
      setTimeout(() => setSuccessMessage(''), 3000);

      // Recargar config local y global (sidebar + otras páginas)
      loadConfiguracion();
      refreshConfig();
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setErrorMessage(error.message || 'Error al guardar la configuración');
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SettingsContainer>
        <PageHeader>
          <Title>Configuración</Title>
        </PageHeader>
        <div>Cargando configuración...</div>
      </SettingsContainer>
    );
  }

  // ── Pestañas ────────────────────────────────────────────────────────────────
  const TABS_DISPONIBLES = [
    { key: 'pipeline',     label: 'Pipeline',         section: 'Ventas' },
    { key: 'leads',        label: 'Leads',            section: 'Ventas' },
    { key: 'cotizaciones', label: 'Cotizaciones',     section: 'Ventas' },
    { key: 'inventario',   label: config?.tipo_negocio === 'servicios' ? 'Servicios' : 'Inventario', section: 'Ventas' },
    { key: 'pedidos',      label: config?.tipo_negocio === 'servicios' ? 'Proyectos' : 'Pedidos',    section: 'Ventas' },
    { key: 'clientes',     label: 'Clientes',         section: 'Clientes' },
    { key: 'grupos',       label: 'Grupos',           section: 'Clientes' },
    { key: 'recibos',      label: 'Recibos',          section: 'Clientes' },
    { key: 'alertas',      label: 'Cobranza',         section: 'Gestión' },
    { key: 'difusion',     label: 'Difusión',         section: 'Gestión' },
    { key: 'plantillas',   label: 'Plantillas WA',    section: 'Gestión' },
    { key: 'mi_dia',       label: 'Mi Día',           section: 'Análisis' },
    { key: 'dashboard',    label: 'Dashboard',        section: 'Análisis' },
    { key: 'reportes',     label: 'Reportes',         section: 'Análisis' },
  ];

  const tabsOcultas = (() => {
    try { return JSON.parse(formData.tabs_ocultas || '[]'); } catch { return []; }
  })();

  const toggleTab = (key) => {
    const next = tabsOcultas.includes(key)
      ? tabsOcultas.filter(k => k !== key)
      : [...tabsOcultas, key];
    setFormData(prev => ({ ...prev, tabs_ocultas: JSON.stringify(next) }));
  };

  const tabSections = [...new Set(TABS_DISPONIBLES.map(t => t.section))];

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return (
          <>
            <SectionTitle>Información General de la Empresa</SectionTitle>
            <SectionDescription>
              Actualiza los datos de tu empresa y personaliza el logotipo.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <LogoSection>
                <LogoPreview>
                  {formData.logo_url ? (
                    <img src={formData.logo_url} alt="Logo de la empresa" />
                  ) : (
                    <MdImage />
                  )}
                </LogoPreview>
                <LogoInfo>
                  <LogoTitle>Logotipo de la Empresa</LogoTitle>
                  <LogoDescription>
                    Sube una imagen de tu logo (PNG, JPG, GIF, WEBP - máx. 2MB).
                  </LogoDescription>
                  <UploadButton onClick={handleLogoClick}>
                    {formData.logo_url ? 'Cambiar Logo' : 'Subir Logo'}
                  </UploadButton>
                  <HiddenFileInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleLogoChange}
                  />
                </LogoInfo>
              </LogoSection>

              <FormGrid>
                <FormGroup>
                  <Label>Nombre de la Empresa</Label>
                  <Input 
                    type="text"
                    name="nombre_empresa"
                    value={formData.nombre_empresa}
                    onChange={handleInputChange}
                    placeholder="Nombre de tu empresa"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Dirección</Label>
                  <Input 
                    type="text"
                    name="direccion"
                    value={formData.direccion}
                    onChange={handleInputChange}
                    placeholder="Dirección completa"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Correo Electrónico de Contacto</Label>
                  <Input 
                    type="email"
                    name="email_contacto"
                    value={formData.email_contacto}
                    onChange={handleInputChange}
                    placeholder="email@empresa.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Número de Teléfono</Label>
                  <Input 
                    type="tel"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    placeholder="+1 234 567 8900"
                  />
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'payments':
        return (
          <>
            <SectionTitle>Pagos y Recibos</SectionTitle>
            <SectionDescription>
              Configura las opciones de pago y personaliza tus recibos.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <FormGrid>
                <FormGroup>
                  <Label>Moneda Predeterminada</Label>
                  <Select
                    name="moneda"
                    value={formData.moneda}
                    onChange={handleInputChange}
                  >
                    <option value="MXN ($)">MXN ($)</option>
                    <option value="USD ($)">USD ($)</option>
                    <option value="EUR (€)">EUR (€)</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Días de Gracia para Pagos</Label>
                  <Input 
                    type="number"
                    name="dias_gracia"
                    value={formData.dias_gracia}
                    onChange={handleInputChange}
                    placeholder="7"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Prefijo de Factura</Label>
                  <Input 
                    type="text"
                    name="prefijo_factura"
                    value={formData.prefijo_factura}
                    onChange={handleInputChange}
                    placeholder="INV-"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>IVA (%)</Label>
                  <Input
                    type="number"
                    name="iva"
                    value={formData.iva}
                    onChange={handleInputChange}
                    placeholder="0"
                    step="0.01"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Código para Cancelar/Reembolsar Recibos</Label>
                  <Input
                    type="text"
                    name="codigo_cancelacion_recibos"
                    value={formData.codigo_cancelacion_recibos}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormGrid>
              <SectionDescription style={{ marginTop: '-8px' }}>
                Este código se pedirá como confirmación antes de cancelar o reembolsar un recibo, y también antes de enviar todos los clientes a la papelera o vaciarla desde Configuración → Importar Clientes, para evitar hacerlo por accidente.
              </SectionDescription>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'notifications':
        return (
          <>
            <SectionTitle>Notificaciones</SectionTitle>
            <SectionDescription>
              Configura cómo y cuándo recibir notificaciones.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <FormGrid>
                <FormGroup>
                  <Label>Email de Notificaciones</Label>
                  <Input
                    type="email"
                    name="email_notificaciones"
                    value={formData.email_notificaciones}
                    onChange={handleInputChange}
                    placeholder="email@empresa.com"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Recordatorio de Pago (días antes)</Label>
                  <Input
                    type="number"
                    name="recordatorio_dias"
                    value={formData.recordatorio_dias}
                    onChange={handleInputChange}
                    placeholder="3"
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Frecuencia de Reportes</Label>
                  <Select
                    name="frecuencia_reportes"
                    value={formData.frecuencia_reportes}
                    onChange={handleInputChange}
                  >
                    <option value="Diario">Diario</option>
                    <option value="Semanal">Semanal</option>
                    <option value="Mensual">Mensual</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'appearance':
        return (
          <>
            <SectionTitle>Apariencia</SectionTitle>
            <SectionDescription>
              Personaliza la apariencia de la aplicación.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <FormGrid>
                <FormGroup>
                  <Label>Tema</Label>
                  <Select
                    name="tema"
                    value={themeName}
                    onChange={(e) => {
                      const newTheme = e.target.value;
                      setTheme(newTheme);
                      setFormData(prev => ({ ...prev, tema: newTheme }));
                    }}
                  >
                    <option value="Claro">Claro</option>
                    <option value="Oscuro">Oscuro</option>
                    <option value="Auto">Automático</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Color Principal</Label>
                  <ColorPickerContainer>
                    <ColorPreviewWrapper>
                      <ColorPreview color={formData.color_principal || '#5B9BD5'} />
                      <ColorInput 
                        type="text"
                        name="color_principal"
                        value={formData.color_principal || '#5B9BD5'}
                        onChange={handleColorInputChange}
                        placeholder="#5B9BD5"
                        maxLength={7}
                      />
                    </ColorPreviewWrapper>
                    <ColorLabel>Colores predefinidos</ColorLabel>
                    <ColorPaletteGrid>
                      {presetColors.map(color => (
                        <ColorOption 
                          key={color}
                          color={color}
                          selected={formData.color_principal === color}
                          onClick={(e) => {
                            e.preventDefault();
                            handleColorChange(color);
                          }}
                          title={color}
                        />
                      ))}
                    </ColorPaletteGrid>
                    <PreviewNote>
                      <MdInfo size={18} />
                      <span>El color se aplicará cuando guardes los cambios</span>
                    </PreviewNote>
                  </ColorPickerContainer>
                </FormGroup>

                <FormGroup>
                  <Label>Idioma</Label>
                  <Select
                    name="idioma"
                    value={formData.idioma}
                    onChange={handleInputChange}
                  >
                    <option value="Español">Español</option>
                    <option value="English">English</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'templates':
        return (
          <>
            <SectionTitle>Personalización de Plantillas</SectionTitle>
            <SectionDescription>
              Personaliza cómo se verán los recibos y comprobantes de pago.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            {/* Plantilla de Recibos */}
            <FormSection>
              <SubsectionTitle>📄 Plantilla de Recibos</SubsectionTitle>
              
              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_logo"
                    name="plantilla_recibo_mostrar_logo"
                    checked={formData.plantilla_recibo_mostrar_logo}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_logo">
                    Mostrar logo de la empresa
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_direccion"
                    name="plantilla_recibo_mostrar_direccion"
                    checked={formData.plantilla_recibo_mostrar_direccion}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_direccion">
                    Mostrar dirección de la empresa
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_telefono"
                    name="plantilla_recibo_mostrar_telefono"
                    checked={formData.plantilla_recibo_mostrar_telefono}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_telefono">
                    Mostrar teléfono de contacto
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_recibo_mostrar_email"
                    name="plantilla_recibo_mostrar_email"
                    checked={formData.plantilla_recibo_mostrar_email}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_recibo_mostrar_email">
                    Mostrar email de contacto
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <Label>Notas Adicionales (aparecen en el recibo)</Label>
                <TextArea
                  name="plantilla_recibo_notas_adicionales"
                  value={formData.plantilla_recibo_notas_adicionales || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Favor de realizar el pago antes de la fecha de vencimiento."
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <Label>Términos y Condiciones</Label>
                <TextArea
                  name="plantilla_recibo_terminos"
                  value={formData.plantilla_recibo_terminos || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Los pagos vencidos generan intereses moratorios..."
                  rows="3"
                />
              </FormGroup>

              <FormGroup>
                <Label>Pie de Página</Label>
                <TextArea
                  name="plantilla_recibo_pie_pagina"
                  value={formData.plantilla_recibo_pie_pagina || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Este documento es un comprobante de la deuda pendiente."
                  rows="2"
                />
              </FormGroup>
            </FormSection>

            {/* Plantilla de Comprobantes */}
            <FormSection style={{ marginTop: '30px' }}>
              <SubsectionTitle>🧾 Plantilla de Comprobantes de Pago</SubsectionTitle>
              
              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_comprobante_mostrar_logo"
                    name="plantilla_comprobante_mostrar_logo"
                    checked={formData.plantilla_comprobante_mostrar_logo}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_comprobante_mostrar_logo">
                    Mostrar logo de la empresa
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="plantilla_comprobante_mostrar_firma"
                    name="plantilla_comprobante_mostrar_firma"
                    checked={formData.plantilla_comprobante_mostrar_firma}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="plantilla_comprobante_mostrar_firma">
                    Mostrar espacio para firma
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              <FormGroup>
                <Label>Mensaje de Agradecimiento</Label>
                <Input
                  type="text"
                  name="plantilla_comprobante_mensaje_agradecimiento"
                  value={formData.plantilla_comprobante_mensaje_agradecimiento || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Gracias por su pago"
                />
              </FormGroup>

              <FormGroup>
                <Label>Pie de Página del Comprobante</Label>
                <TextArea
                  name="plantilla_comprobante_pie_pagina"
                  value={formData.plantilla_comprobante_pie_pagina || ''}
                  onChange={handleInputChange}
                  placeholder="Ej: Para cualquier aclaración, contacte con nosotros."
                  rows="2"
                />
              </FormGroup>
            </FormSection>

            {/* Formatos */}
            <FormSection style={{ marginTop: '30px' }}>
              <SubsectionTitle>⚙️ Formatos</SubsectionTitle>
              
              <FormGrid>
                <FormGroup>
                  <Label>Formato de Fecha</Label>
                  <Select
                    name="formato_fecha"
                    value={formData.formato_fecha}
                    onChange={handleInputChange}
                  >
                    <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2025)</option>
                    <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2025)</option>
                    <option value="YYYY-MM-DD">YYYY-MM-DD (2025-12-31)</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Formato de Moneda</Label>
                  <Select
                    name="formato_moneda"
                    value={formData.formato_moneda}
                    onChange={handleInputChange}
                  >
                    <option value="$0,0.00">$1,234.56</option>
                    <option value="$0.0,00">$1.234,56</option>
                    <option value="0,0.00">1,234.56</option>
                  </Select>
                </FormGroup>
              </FormGrid>

              <FormGroup>
                <CheckboxContainer>
                  <Checkbox
                    type="checkbox"
                    id="mostrar_sello_digital"
                    name="mostrar_sello_digital"
                    checked={formData.mostrar_sello_digital}
                    onChange={handleCheckboxChange}
                  />
                  <CheckboxLabel htmlFor="mostrar_sello_digital">
                    Mostrar sello digital/código QR
                  </CheckboxLabel>
                </CheckboxContainer>
              </FormGroup>

              {formData.mostrar_sello_digital && (
                <FormGroup>
                  <Label>Texto para el Sello Digital</Label>
                  <Input
                    type="text"
                    name="texto_sello_digital"
                    value={formData.texto_sello_digital || ''}
                    onChange={handleInputChange}
                    placeholder="Ej: Documento verificado digitalmente"
                  />
                </FormGroup>
              )}

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'printing':
        return (
          <>
            <SectionTitle>Configuración de Impresión</SectionTitle>
            <SectionDescription>
              Configura el formato de impresión para tus documentos. Elige entre formato normal (A4) o formato térmico para impresoras de tickets.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <FormGroup>
                <Label>Formato de Impresión</Label>
                <Select
                  name="formato_impresion"
                  value={formData.formato_impresion || 'normal'}
                  onChange={handleInputChange}
                >
                  <option value="normal">Normal (A4 / Carta)</option>
                  <option value="termica">Térmica (Ticket)</option>
                </Select>
                <InfoText>
                  <MdInfo /> El formato térmico es ideal para impresoras de tickets de 80mm o 58mm.
                </InfoText>
              </FormGroup>

              {formData.formato_impresion === 'termica' && (
                <FormGroup>
                  <Label>Ancho del Papel Térmico</Label>
                  <Select
                    name="ancho_papel_termico"
                    value={formData.ancho_papel_termico || 80}
                    onChange={handleInputChange}
                  >
                    <option value={80}>80mm (3 pulgadas) - Recomendado</option>
                    <option value={58}>58mm (2.25 pulgadas)</option>
                  </Select>
                  <InfoText>
                    <MdInfo /> La mayoría de impresoras térmicas comerciales usan papel de 80mm.
                  </InfoText>
                </FormGroup>
              )}

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'ycloud':
        return (
          <>
            <SectionTitle style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FaWhatsapp style={{ color: '#25D366' }} /> YCloud / WhatsApp
            </SectionTitle>
            <SectionDescription>
              Configura la API Key y el número remitente para enviar mensajes de WhatsApp a través de YCloud.
            </SectionDescription>

            {successMessage ? (<SuccessMessage><MdCheck /> {successMessage}</SuccessMessage>) : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            {/* ── Credenciales YCloud ── */}
            <FormSection>
              <div style={{ marginBottom: '20px', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', fontSize: '13px', color: '#166534' }}>
                Crea una cuenta en <a href="https://app.ycloud.com" target="_blank" rel="noopener noreferrer" style={{ color: '#15803d' }}>app.ycloud.com</a>{' '}
                y copia aquí tu API Key y el número de WhatsApp remitente verificado.
              </div>

              <FormGrid>
                <FormGroup>
                  <Label>API Key de yCloud</Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      name="ycloud_api_key"
                      value={formData.ycloud_api_key || ''}
                      onChange={handleInputChange}
                      placeholder="yk_live_..."
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(v => !v)}
                      style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      title={showApiKey ? 'Ocultar' : 'Mostrar'}
                    >
                      {showApiKey ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  <InfoText><MdInfo /> Encuéntrala en yCloud → Configuración → API Keys</InfoText>
                </FormGroup>

                <FormGroup>
                  <Label>Número de WhatsApp remitente</Label>
                  <Input
                    type="text"
                    name="ycloud_whatsapp_numero"
                    value={formData.ycloud_whatsapp_numero || ''}
                    onChange={handleInputChange}
                    placeholder="+521234567890"
                  />
                  <InfoText><MdInfo /> Formato E.164, ej: +521234567890</InfoText>
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>

            {/* ── Plantilla de Cobranza ── */}
            <FormSection style={{ marginTop: '8px', paddingTop: '24px', borderTop: '1px solid', borderColor: 'inherit' }}>
              <div style={{ marginBottom: '6px', fontSize: '1rem', fontWeight: 700 }}>Plantilla de cobranza</div>
              <SectionDescription>
                El sistema envía recordatorios automáticos de pago cada día a las 9 AM a clientes con
                recibos vencidos o próximos a vencer. Asigne aquí la plantilla aprobada que se usará
                para esos mensajes.
              </SectionDescription>

              <FormGroup style={{ maxWidth: 480, marginBottom: '16px' }}>
                <Label>Nombre de la plantilla</Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Input
                    type="text"
                    name="ycloud_plantilla_nombre"
                    value={formData.ycloud_plantilla_nombre || ''}
                    onChange={handleInputChange}
                    placeholder="ej. recordatorio_cobro"
                    style={{ flex: 1 }}
                  />
                  {formData.ycloud_plantilla_nombre && (
                    <span style={{ fontSize: '0.8rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                      ( {formData.ycloud_plantilla_idioma || 'es'} )
                    </span>
                  )}
                </div>
                {formData.ycloud_plantilla_nombre && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', fontSize: '0.78rem', color: '#64748b' }}>
                    <span>Idioma detectado:</span>
                    <span style={{ fontWeight: 700, color: '#0f172a' }}>{formData.ycloud_plantilla_idioma || 'es'}</span>
                    <select
                      name="ycloud_plantilla_idioma"
                      value={formData.ycloud_plantilla_idioma || 'es'}
                      onChange={handleInputChange}
                      style={{ marginLeft: 4, fontSize: '0.78rem', padding: '2px 6px', borderRadius: 6, border: '1px solid #cbd5e1', background: 'transparent', cursor: 'pointer' }}
                    >
                      <option value="es">es</option>
                      <option value="es_MX">es_MX</option>
                      <option value="en">en</option>
                      <option value="en_US">en_US</option>
                    </select>
                  </div>
                )}
              </FormGroup>

              <div style={{ padding: '12px 16px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '13px', color: '#78350f', marginBottom: '16px' }}>
                <strong>La plantilla de cobranza debe tener 4 variables:</strong>{' '}
                {'{{1}}'} nombre, {'{{2}}'} recibo, {'{{3}}'} monto, {'{{4}}'} vencimiento.
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <SaveButton onClick={handleSave} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar asignación'}
                </SaveButton>
                <span style={{ fontSize: '0.78rem', color: '#64748b', fontStyle: 'italic' }}>
                  La gestión de mensajes se hace desde la pestaña de Cobranza
                </span>
              </div>
            </FormSection>
          </>
        );

      case 'negocio':
        return (
          <>
            <SectionTitle>Tipo de Negocio</SectionTitle>
            <SectionDescription>
              Configura si tu negocio ofrece productos fisicos o servicios. Esto determina el comportamiento del modulo de Inventario.
            </SectionDescription>

            {successMessage ? <SuccessMessage><MdCheck /> {successMessage}</SuccessMessage> : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <Label>Selecciona el tipo de negocio</Label>
              <BusinessTypeRow>
                <BusinessTypeCard
                  $active={formData.tipo_negocio === 'productos'}
                  onClick={() => setFormData(prev => ({ ...prev, tipo_negocio: 'productos' }))}
                >
                  <MdInventory2 />
                  <h4>Productos</h4>
                  <p>Inventario con stock, SKU y control de existencias</p>
                </BusinessTypeCard>
                <BusinessTypeCard
                  $active={formData.tipo_negocio === 'servicios'}
                  onClick={() => setFormData(prev => ({ ...prev, tipo_negocio: 'servicios' }))}
                >
                  <MdDesignServices />
                  <h4>Servicios</h4>
                  <p>Catalogo de servicios con duracion y descripcion</p>
                </BusinessTypeCard>
              </BusinessTypeRow>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'ia':
        return (
          <>
            <SectionTitle>Inteligencia Artificial</SectionTitle>
            <SectionDescription>
              Conecta el asistente de IA con Anthropic (Claude) u OpenAI (GPT). Elige el proveedor, pega tu API Key y selecciona el modelo.
            </SectionDescription>

            {successMessage ? <SuccessMessage><MdCheck /> {successMessage}</SuccessMessage> : null}
            {errorMessage ? <ErrorMessage>{errorMessage}</ErrorMessage> : null}

            <FormSection>
              <Label>Proveedor</Label>
              <BusinessTypeRow style={{ marginTop: '8px' }}>
                <BusinessTypeCard
                  $active={!formData.ai_provider || formData.ai_provider === 'anthropic'}
                  onClick={() => setFormData(prev => ({ ...prev, ai_provider: 'anthropic', ai_model: 'claude-sonnet-5' }))}
                >
                  <MdSmartToy />
                  <h4>Anthropic</h4>
                  <p>Claude Sonnet, Opus y Haiku</p>
                </BusinessTypeCard>
                <BusinessTypeCard
                  $active={formData.ai_provider === 'openai'}
                  onClick={() => setFormData(prev => ({ ...prev, ai_provider: 'openai', ai_model: 'gpt-4o' }))}
                >
                  <MdSmartToy />
                  <h4>OpenAI</h4>
                  <p>GPT-4o, GPT-4.1 y modelos mini</p>
                </BusinessTypeCard>
              </BusinessTypeRow>

              <FormGrid style={{ marginTop: '8px' }}>
                <FormGroup>
                  <Label>
                    {formData.ai_provider === 'openai' ? 'API Key de OpenAI' : 'API Key de Anthropic'}
                  </Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Input
                      type={showApiKey ? 'text' : 'password'}
                      name="ai_api_key"
                      value={formData.ai_api_key || ''}
                      onChange={handleInputChange}
                      placeholder={formData.ai_provider === 'openai' ? 'sk-proj-...' : 'sk-ant-api03-...'}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowApiKey(v => !v)}
                      style={{ padding: '8px 12px', border: '1px solid #ddd', borderRadius: '8px', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      title={showApiKey ? 'Ocultar' : 'Mostrar'}
                    >
                      {showApiKey ? <MdVisibilityOff /> : <MdVisibility />}
                    </button>
                  </div>
                  <InfoText>
                    <MdInfo />
                    {formData.ai_provider === 'openai'
                      ? <> Obtén tu clave en <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">platform.openai.com</a>. La clave se guarda en tu servidor.</>
                      : <> Obtén tu clave en <a href="https://console.anthropic.com" target="_blank" rel="noopener noreferrer">console.anthropic.com</a>. La clave se guarda en tu servidor.</>
                    }
                  </InfoText>
                </FormGroup>

                <FormGroup>
                  <Label>Modelo</Label>
                  <Select
                    name="ai_model"
                    value={formData.ai_model || (formData.ai_provider === 'openai' ? 'gpt-4o' : 'claude-sonnet-5')}
                    onChange={handleInputChange}
                  >
                    {formData.ai_provider === 'openai' ? (
                      <>
                        <option value="gpt-4o">GPT-4o — Más capaz y versátil (recomendado)</option>
                        <option value="gpt-4o-mini">GPT-4o mini — Rápido y económico</option>
                        <option value="gpt-4.1">GPT-4.1 — Última generación (2025)</option>
                        <option value="gpt-4.1-mini">GPT-4.1 mini — Económico y rápido</option>
                      </>
                    ) : (
                      <>
                        <option value="claude-sonnet-5">Claude Sonnet 5 — Balanceado (recomendado)</option>
                        <option value="claude-opus-5">Claude Opus 5 — Máxima capacidad</option>
                        <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 — Más rápido y económico</option>
                      </>
                    )}
                  </Select>
                  <InfoText>
                    <MdInfo />
                    {formData.ai_provider === 'openai'
                      ? 'GPT-4o ofrece la mejor relación calidad-precio para un CRM.'
                      : 'Sonnet es el mejor balance entre velocidad, costo y calidad.'}
                  </InfoText>
                </FormGroup>
              </FormGrid>

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Configuración IA'}
              </SaveButton>
            </FormSection>
          </>
        );

      case 'tabs':
        return (
          <>
            <SectionTitle>Pestañas del menú</SectionTitle>
            <SectionDescription>
              Activa o desactiva las secciones del menú lateral. Las desactivadas dejan de aparecer para los usuarios con rol "Usuario" —
              los administradores siempre ven todas las pestañas, para no perder acceso por accidente.
            </SectionDescription>

            <CheckboxContainer
              style={{ marginBottom: 20, cursor: 'pointer' }}
              onClick={() => setFormData(prev => ({ ...prev, ocultar_tabs_admin: !prev.ocultar_tabs_admin }))}
            >
              <Checkbox
                type="checkbox"
                name="ocultar_tabs_admin"
                checked={formData.ocultar_tabs_admin}
                onChange={handleCheckboxChange}
                onClick={e => e.stopPropagation()}
              />
              <CheckboxLabel style={{ cursor: 'pointer' }}>
                Aplicar estas pestañas ocultas también a mi propia cuenta de administrador
              </CheckboxLabel>
            </CheckboxContainer>

            <FormSection>
              {tabSections.map(section => (
                <div key={section} style={{ marginBottom: 24 }}>
                  <SubsectionTitle style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                    {section}
                  </SubsectionTitle>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                    {TABS_DISPONIBLES.filter(t => t.section === section).map(tab => {
                      const visible = !tabsOcultas.includes(tab.key);
                      return (
                        <CheckboxContainer
                          key={tab.key}
                          style={{
                            padding: '10px 14px',
                            background: visible
                              ? 'transparent'
                              : 'rgba(239,68,68,0.04)',
                            border: `1px solid ${visible ? 'var(--border, #e2e8f0)' : 'rgba(239,68,68,0.2)'}`,
                            borderRadius: 10,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                          onClick={() => toggleTab(tab.key)}
                        >
                          <Checkbox
                            type="checkbox"
                            checked={visible}
                            onChange={() => toggleTab(tab.key)}
                            onClick={e => e.stopPropagation()}
                          />
                          <CheckboxLabel style={{ cursor: 'pointer', opacity: visible ? 1 : 0.5 }}>
                            {tab.label}
                          </CheckboxLabel>
                        </CheckboxContainer>
                      );
                    })}
                  </div>
                </div>
              ))}

              {tabsOcultas.length > 0 && (
                <InfoText style={{ marginBottom: 16 }}>
                  <MdVisibilityOff style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {tabsOcultas.length} pestaña{tabsOcultas.length !== 1 ? 's' : ''} oculta{tabsOcultas.length !== 1 ? 's' : ''}: {tabsOcultas.join(', ')}
                </InfoText>
              )}

              <SaveButton onClick={handleSave} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </SaveButton>
            </FormSection>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <SettingsContainer>
      <PageHeader>
        <Title>Configuración</Title>
      </PageHeader>

      <SettingsLayout>
        <SettingsSidebar>
          <SidebarItem 
            active={activeSection === 'general'} 
            onClick={() => setActiveSection('general')}
          >
            <MdSettings />
            <span>General</span>
          </SidebarItem>

          <SidebarItem 
            active={activeSection === 'payments'} 
            onClick={() => setActiveSection('payments')}
          >
            <MdPayment />
            <span>Pagos y Recibos</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'notifications'}
            onClick={() => setActiveSection('notifications')}
          >
            <MdNotifications />
            <span>Notificaciones</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'ycloud'}
            onClick={() => setActiveSection('ycloud')}
          >
            <FaWhatsapp />
            <span>YCloud / WhatsApp</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'appearance'} 
            onClick={() => setActiveSection('appearance')}
          >
            <MdPalette />
            <span>Apariencia</span>
          </SidebarItem>

          <SidebarItem 
            active={activeSection === 'templates'} 
            onClick={() => setActiveSection('templates')}
          >
            <MdDescription />
            <span>Plantillas</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'printing'}
            onClick={() => setActiveSection('printing')}
          >
            <MdPrint />
            <span>Impresión</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'negocio'}
            onClick={() => setActiveSection('negocio')}
          >
            <MdInventory2 />
            <span>Tipo de Negocio</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'ia'}
            onClick={() => setActiveSection('ia')}
          >
            <MdSmartToy />
            <span>Inteligencia Artificial</span>
          </SidebarItem>

          <SidebarItem
            active={activeSection === 'tabs'}
            onClick={() => setActiveSection('tabs')}
          >
            <MdVisibility />
            <span>Pestañas</span>
          </SidebarItem>

          <SidebarItem onClick={() => navigate('/configuracion/importar-clientes')}>
            <MdUploadFile />
            <span>Importar Clientes</span>
          </SidebarItem>

        </SettingsSidebar>

        <SettingsContent>
          {renderContent()}
        </SettingsContent>
      </SettingsLayout>
    </SettingsContainer>
  );
};

export default Settings;
